import { VialLabelPrinterConfig } from './zplTemplates';

/**
 * Single source of truth for the vial-label Zebra printer config.
 *
 * Every consumer of the saved config (ZplTestPage, LabelPrintingModal,
 * PackagingVerification, ProductVerificationDialog) must go through this
 * module. Before 2026-07-22 the defaults were triplicated and divergent:
 * the test page defaulted to T90/L5 on a 2" head while the real print path
 * fell back to T160/L10 on a 4" head — so any station with fresh/cleared
 * localStorage printed real labels with values no pharmacy uses.
 */

export const VIAL_PRINTER_CONFIG_STORAGE_KEY = 'greenshield_vial_printer_config';

/**
 * Bumped when the stored shape/defaults change. Copies saved without a
 * version stamp predate the 2026-07-22 margin correction (see load below).
 */
export const VIAL_PRINTER_CONFIG_VERSION = 2;

/** What every pharmacy actually runs: 2" head @ 203dpi, centered, POI, T-37/L5. */
export const DEFAULT_VIAL_PRINTER_CONFIG: VialLabelPrinterConfig = {
  dpi: 203,
  printheadWidthDots: 406,
  centerMedia: true,
  rotate180: true,
  marginTop: -37,
  marginLeft: 5,
};

/** The default that shipped before 2026-07-22 (wrong for every pharmacy). */
const LEGACY_DEFAULT_MARGIN_TOP = 90;
const LEGACY_DEFAULT_MARGIN_LEFT = 5;

/** Stored shape: the printer config plus storage-only bookkeeping fields. */
export interface StoredVialPrinterConfig extends VialLabelPrinterConfig {
  configVersion?: number;
  /** UI convenience kept by ZplTestPage alongside printheadWidthDots. */
  printheadSizeInches?: number;
}

/**
 * Load the station's saved vial printer config, falling back to
 * DEFAULT_VIAL_PRINTER_CONFIG for anything missing.
 *
 * Migration (approved in the 2026-07-22 hotfix PR): an UNVERSIONED saved
 * copy still sitting exactly on the old shipped default margins (T90/L5)
 * was never customized — it's a station that clicked Save on the wrong
 * default. Those get the corrected margins (T-37/L5). Any other saved
 * margins, and any version-stamped copy, are the station's deliberate
 * calibration and are preserved verbatim.
 */
export function loadSavedVialPrinterConfig(): StoredVialPrinterConfig {
  try {
    const saved = localStorage.getItem(VIAL_PRINTER_CONFIG_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as StoredVialPrinterConfig;
      const config: StoredVialPrinterConfig = { ...DEFAULT_VIAL_PRINTER_CONFIG, ...parsed };
      const isUnversioned = parsed.configVersion === undefined;
      const onLegacyDefaultMargins =
        parsed.marginTop === LEGACY_DEFAULT_MARGIN_TOP &&
        parsed.marginLeft === LEGACY_DEFAULT_MARGIN_LEFT;
      if (isUnversioned && onLegacyDefaultMargins) {
        config.marginTop = DEFAULT_VIAL_PRINTER_CONFIG.marginTop;
        config.marginLeft = DEFAULT_VIAL_PRINTER_CONFIG.marginLeft;
      }
      return config;
    }
  } catch (e) {
    console.warn('[VialPrinterConfig] Failed to load saved config:', e);
  }
  return { ...DEFAULT_VIAL_PRINTER_CONFIG };
}

/** Persist the station's vial printer config, stamping the storage version. */
export function saveVialPrinterConfig(config: StoredVialPrinterConfig): void {
  try {
    localStorage.setItem(
      VIAL_PRINTER_CONFIG_STORAGE_KEY,
      JSON.stringify({ ...config, configVersion: VIAL_PRINTER_CONFIG_VERSION }),
    );
  } catch (e) {
    console.warn('[VialPrinterConfig] Failed to save config:', e);
  }
}
